'use client';

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { BookHeart, Info, X, ChevronDown } from 'lucide-react';

interface MontessoriToggleProps {
  enabled: boolean;
  isSubscriber: boolean;
  isSignedIn: boolean;
  onChange: (next: boolean) => void;
  onUpsell: () => void;
}

export default function MontessoriToggle({
  enabled,
  isSubscriber,
  isSignedIn,
  onChange,
  onUpsell,
}: MontessoriToggleProps) {
  const router = useRouter();
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedBenefit, setExpandedBenefit] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showLearnMore && mounted) {
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;
      
      // Prevent scroll
      document.body.style.overflow = 'hidden';
      
      // Prevent layout shift by adding padding for scrollbar width
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [showLearnMore, mounted]);

  const handleClick = () => {
    // Scenario 3: Has subscription → Toggle works normally
    if (isSubscriber) {
      onChange(!enabled);
      return;
    }
    
    // Scenario 1: Not signed in → Redirect to login
    if (!isSignedIn) {
      // Store current page for post-login redirect
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('montessori-redirect', window.location.pathname);
      }
      // Redirect to login page with callback
      const callbackUrl = encodeURIComponent(window.location.pathname);
      router.push(`/login?callbackUrl=${callbackUrl}`);
      return;
    }
    
    // Scenario 2: Signed in, no subscription → Show upsell modal
    onUpsell();
  };

  return (
    <div
      className="montessori-card-2026"
      style={{
        background: 'white',
        borderRadius: '20px',
        padding: '1.25rem',
        border: enabled ? '2px solid rgba(139, 195, 74, 0.5)' : '2px solid rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: enabled 
          ? '0 4px 16px rgba(139, 195, 74, 0.2)' 
          : '0 2px 8px rgba(0, 0, 0, 0.05)',
        position: 'relative',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = enabled
          ? '0 6px 24px rgba(139, 195, 74, 0.3)'
          : '0 4px 12px rgba(0, 0, 0, 0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = enabled
          ? '0 4px 16px rgba(139, 195, 74, 0.2)'
          : '0 2px 8px rgba(0, 0, 0, 0.05)';
      }}
    >
      {/* Mobile: Compact Collapsible Header */}
      <div 
        className="mobile-compact-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'none', // Will be shown on mobile via CSS
          cursor: 'pointer',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
        }}>
          {/* Icon */}
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #a8d5e2, #8bc9e0)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(139, 195, 74, 0.2)',
            }}
          >
            <BookHeart size={24} color="white" strokeWidth={2.5} />
          </div>

          {/* Title & Status */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap',
            }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  color: '#2c3e50',
                }}
              >
                Montessori
              </h3>
              {enabled && (
                <span
                  style={{
                    padding: '0.125rem 0.5rem',
                    background: 'linear-gradient(135deg, #8BC34A, #CDDC39)',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '0.625rem',
                    fontWeight: 800,
                    boxShadow: '0 2px 4px rgba(139, 195, 74, 0.3)',
                  }}
                >
                  ON
                </span>
              )}
            </div>
            {!isSubscriber && (
              <p
                style={{
                  margin: '0.125rem 0 0 0',
                  fontSize: '0.6875rem',
                  color: '#94a3b8',
                  fontWeight: 500,
                }}
              >
                Subscribers only
              </p>
            )}
          </div>

          {/* Toggle Switch */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            style={{
              position: 'relative',
              width: '48px',
              height: '26px',
              borderRadius: '13px',
              background: enabled
                ? 'linear-gradient(135deg, #8BC34A, #CDDC39)'
                : 'rgba(0, 0, 0, 0.15)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              outline: 'none',
              flexShrink: 0,
              boxShadow: enabled ? '0 2px 8px rgba(139, 195, 74, 0.3)' : 'none',
            }}
            aria-label={enabled ? 'Disable Montessori Mode' : 'Enable Montessori Mode'}
          >
            <div
              style={{
                position: 'absolute',
                top: '2px',
                left: enabled ? '24px' : '2px',
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </button>

          {/* Expand/Collapse Chevron */}
          <div
            style={{
              flexShrink: 0,
              color: '#94a3b8',
              transition: 'transform 0.3s ease',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <ChevronDown size={20} />
          </div>
        </div>
      </div>

      {/* Mobile: Expandable Content */}
      <div
        className="mobile-expandable-content"
        style={{
          maxHeight: isExpanded ? '400px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'none', // Will be shown on mobile via CSS
        }}
      >
        <div style={{ 
          paddingTop: '1rem', 
          marginTop: '1rem', 
          borderTop: '1px solid rgba(0, 0, 0, 0.06)' 
        }}>
          {/* Description */}
          <p
            style={{
              margin: '0 0 1rem 0',
              fontSize: '0.875rem',
              color: '#64748b',
              lineHeight: 1.6,
            }}
          >
            Scientifically-backed learning approach that helps children develop naturally through interactive content
          </p>

          {/* Learn More Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowLearnMore(true);
            }}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              background: 'linear-gradient(135deg, #8BC34A 0%, #CDDC39 100%)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(139, 195, 74, 0.25)',
              minHeight: '44px',
            }}
          >
            <Info size={16} strokeWidth={2.5} />
            <span>Learn More</span>
          </button>
        </div>
      </div>

      {/* Desktop: Full Layout (Always Visible) */}
      <div 
        className="desktop-full-layout"
        onClick={handleClick}
        style={{
          cursor: 'pointer',
        }}
      >
        <div 
          className="montessori-layout"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
          }}
        >
        {/* Header: Icon + Toggle (Mobile: centered, Desktop: left aligned) */}
        <div 
          className="montessori-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          {/* Icon - Larger and centered on mobile */}
          <div
            className="montessori-icon"
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #a8d5e2, #8bc9e0)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(139, 195, 74, 0.2)',
              transition: 'all 0.3s ease',
            }}
          >
            <BookHeart size={28} color="white" strokeWidth={2.5} />
          </div>

          {/* Toggle Switch - Larger for mobile */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            className="montessori-toggle"
            style={{
              position: 'relative',
              width: '52px',
              height: '30px',
              borderRadius: '15px',
              background: enabled
                ? 'linear-gradient(135deg, #8BC34A, #CDDC39)'
                : 'rgba(0, 0, 0, 0.15)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              outline: 'none',
              flexShrink: 0,
              boxShadow: enabled ? '0 2px 8px rgba(139, 195, 74, 0.3)' : 'none',
            }}
            aria-label={enabled ? 'Disable Montessori Mode' : 'Enable Montessori Mode'}
          >
            <div
              style={{
                position: 'absolute',
                top: '3px',
                left: enabled ? '26px' : '3px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </button>
        </div>

        {/* Content */}
        <div 
          className="montessori-content"
          style={{
            width: '100%',
          }}
        >
          {/* Title */}
          <h3
            style={{
              margin: '0 0 0.5rem 0',
              fontSize: 'clamp(1rem, 4vw, 1.125rem)',
              fontWeight: 700,
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap',
            }}
          >
            Montessori Learning
            {enabled && (
              <span
                style={{
                  padding: '0.25rem 0.625rem',
                  background: 'linear-gradient(135deg, #8BC34A, #CDDC39)',
                  color: 'white',
                  borderRadius: '10px',
                  fontSize: '0.6875rem',
                  fontWeight: 800,
                  boxShadow: '0 2px 4px rgba(139, 195, 74, 0.3)',
                }}
              >
                ACTIVE
              </span>
            )}
          </h3>

          {/* Description - More readable on mobile */}
          <p
            style={{
              margin: '0 0 1rem 0',
              fontSize: 'clamp(0.875rem, 3.5vw, 0.9375rem)',
              color: '#64748b',
              lineHeight: 1.6,
            }}
          >
            Scientifically-backed learning approach that helps children develop naturally through interactive content
          </p>

          {/* Learn More Button - Mobile-Optimized */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowLearnMore(true);
            }}
            className="learn-more-btn-2026"
            style={{
              padding: '0.625rem 1.25rem',
              background: 'linear-gradient(135deg, #8BC34A 0%, #CDDC39 100%)',
              backgroundSize: '200% 100%',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              fontSize: 'clamp(0.8125rem, 3.5vw, 0.875rem)',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 2px 8px rgba(139, 195, 74, 0.25)',
              position: 'relative',
              overflow: 'hidden',
              isolation: 'isolate',
              minHeight: '44px',
              width: '100%',
            }}
          >
            <Info size={16} strokeWidth={2.5} />
            <span style={{ position: 'relative', zIndex: 1 }}>Learn More</span>
            <style jsx>{`
              .learn-more-btn-2026::before {
                content: '';
                position: absolute;
                top: -50%;
                left: -50%;
                width: 200%;
                height: 200%;
                background: linear-gradient(
                  45deg,
                  transparent 30%,
                  rgba(255, 255, 255, 0.4) 50%,
                  transparent 70%
                );
                transform: rotate(45deg);
                animation: shimmerSweep 2.5s linear infinite;
                z-index: 0;
              }
              
              @keyframes shimmerSweep {
                0% { transform: translateX(-100%) rotate(45deg); }
                100% { transform: translateX(100%) rotate(45deg); }
              }
              
              .learn-more-btn-2026:hover {
                transform: translateY(-2px) scale(1.02);
                box-shadow: 0 4px 16px rgba(139, 195, 74, 0.4);
                background-position: 100% 0;
              }
              
              .learn-more-btn-2026:active {
                transform: translateY(0) scale(0.98);
                box-shadow: 0 2px 8px rgba(139, 195, 74, 0.3);
              }
              
              @media (prefers-reduced-motion: reduce) {
                .learn-more-btn-2026::before {
                  animation: none;
                }
              }
            `}</style>
          </button>

          {!isSubscriber && (
            <p
              style={{
                margin: '0.75rem 0 0 0',
                fontSize: 'clamp(0.75rem, 3vw, 0.8125rem)',
                color: '#94a3b8',
                fontWeight: 500,
                textAlign: 'center',
              }}
            >
              For subscribers only
            </p>
           )}
        </div>
      </div>
      </div>

       {/* Responsive CSS - Mobile vs Desktop Toggle */}
       <style>{`
         /* Mobile: Show compact collapsible version */
         @media (max-width: 768px) {
           .montessori-card-2026 {
             padding: 1rem !important;
           }

           .mobile-compact-header {
             display: block !important;
           }

           .mobile-expandable-content {
             display: block !important;
           }

           .desktop-full-layout {
             display: none !important;
           }
         }

         /* Desktop: Show full layout, hide mobile version */
         @media (min-width: 769px) {
           .mobile-compact-header {
             display: none !important;
           }

           .mobile-expandable-content {
             display: none !important;
           }

           .desktop-full-layout {
             display: block !important;
           }

           .montessori-layout {
             flex-direction: row !important;
             align-items: center !important;
           }

           .montessori-header {
             flex-shrink: 0 !important;
           }

           .learn-more-btn-2026 {
             width: auto !important;
           }
         }
       `}</style>

      {/* Learn More Modal - 2026 Compact Design */}
      {showLearnMore && mounted && typeof document !== 'undefined' && createPortal(
        <div
          className="montessori-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '1rem',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
            animation: 'fadeIn 0.3s ease-out',
            isolation: 'isolate',
          }}
          onClick={() => {
            setShowLearnMore(false);
            setExpandedBenefit(null);
          }}
        >
          <div
            className="montessori-modal-content"
            style={{
              background: '#FFFFFF',
              borderRadius: '28px',
              padding: 'clamp(1.5rem, 4vw, 2rem)',
              maxWidth: '500px',
              width: '100%',
              maxHeight: 'calc(100dvh - 2rem)',
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2), 0 0 0 2px rgba(139, 195, 74, 0.3) inset',
              position: 'relative',
              animation: 'modalSlideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              border: '2px solid rgba(139, 195, 74, 0.6)',
              zIndex: 1000000,
              isolation: 'isolate',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Subtle Background Pattern */}
            <div style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '28px',
              overflow: 'hidden',
              opacity: 0.03,
              pointerEvents: 'none',
              zIndex: 0,
              background: 'radial-gradient(circle at 20% 30%, rgba(139, 195, 74, 0.4) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(205, 220, 57, 0.3) 0%, transparent 50%)',
            }} />

            {/* Close button */}
            <button
              onClick={() => setShowLearnMore(false)}
              style={{
                position: 'absolute',
                top: 'clamp(0.75rem, 3vw, 1rem)',
                right: 'clamp(0.75rem, 3vw, 1rem)',
                background: '#F3F4F6',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                cursor: 'pointer',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                zIndex: 10,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#E5E7EB';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#F3F4F6';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              aria-label="Close"
            >
              <X size={20} color="#6B7280" strokeWidth={2.5} />
            </button>

            {/* Compact Header with Icon */}
            <div style={{ 
              position: 'relative', 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: '1.25rem',
              zIndex: 1,
            }}>
              {/* Pulsing Glow Background */}
              <div style={{
                position: 'absolute',
                top: '15px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100px',
                height: '100px',
                background: 'radial-gradient(circle, rgba(139, 195, 74, 0.3) 0%, transparent 70%)',
                borderRadius: '50%',
                animation: 'pulse 3s ease-in-out infinite',
                zIndex: 0,
              }} />
              
              {/* Compact Icon */}
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #8BC34A, #CDDC39)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(139, 195, 74, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.3) inset',
                  position: 'relative',
                  zIndex: 2,
                  animation: 'iconFloat 3s ease-in-out infinite',
                  marginBottom: '1rem',
                }}
              >
                <BookHeart size={32} color="white" strokeWidth={2.5} />
              </div>

              {/* Compact Title */}
              <h3
                style={{
                  fontSize: 'clamp(1.5rem, 5vw, 1.75rem)',
                  fontWeight: 900,
                  textAlign: 'center',
                  marginBottom: '0.5rem',
                  background: 'linear-gradient(135deg, #8BC34A 0%, #5D7C1E 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  position: 'relative',
                  zIndex: 1,
                  letterSpacing: '-0.02em',
                }}
              >
                Montessori Learning
              </h3>

              {/* Compact Description */}
              <p
                style={{
                  fontSize: 'clamp(0.875rem, 3.5vw, 0.9375rem)',
                  color: '#64748b',
                  textAlign: 'center',
                  lineHeight: 1.6,
                  marginBottom: '0',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                Scientifically-backed approach by <strong style={{ color: '#2c3e50' }}>Dr. Maria Montessori</strong>
              </p>
            </div>

            {/* Section Title */}
            <h4
              style={{
                fontSize: 'clamp(1rem, 4vw, 1.125rem)',
                fontWeight: 800,
                color: '#2c3e50',
                marginBottom: '0.75rem',
                marginTop: '1.5rem',
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <span style={{
                width: '3px',
                height: '18px',
                background: 'linear-gradient(135deg, #8BC34A, #CDDC39)',
                borderRadius: '3px',
              }} />
              Key Benefits
            </h4>

            {/* Benefits - Accordion Style */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.625rem',
              marginBottom: '1.5rem',
              position: 'relative',
              zIndex: 1,
            }}>
              {[
                { 
                  label: 'Real-world focus', 
                  text: 'Stories feature everyday objects, practical life skills, and familiar experiences that children can relate to',
                  color: '#8BC34A'
                },
                { 
                  label: 'Sensorial development', 
                  text: 'Content emphasizes touch, sight, sound, and other senses to enhance learning',
                  color: '#CDDC39'
                },
                { 
                  label: 'Independence', 
                  text: 'Characters learn through exploration, problem-solving, and self-discovery',
                  color: '#7CB342'
                },
                { 
                  label: 'Nature connection', 
                  text: 'Themes include outdoor activities, natural world exploration, and environmental awareness',
                  color: '#9CCC65'
                },
                { 
                  label: 'Age-appropriate', 
                  text: "Content is carefully designed to match your child's developmental stage",
                  color: '#AED581'
                },
              ].map((benefit, index) => {
                const isExpanded = expandedBenefit === index;
                return (
                  <div
                    key={index}
                    style={{
                      borderRadius: '14px',
                      background: isExpanded ? '#FAFBFC' : '#FFFFFF',
                      border: `2px solid ${isExpanded ? benefit.color : '#E5E7EB'}`,
                      boxShadow: isExpanded ? `0 4px 12px ${benefit.color}30` : '0 1px 3px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      animation: `fadeInUp 0.4s ease-out ${index * 0.08}s backwards`,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Accordion Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedBenefit(isExpanded ? null : index);
                      }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.75rem',
                        padding: 'clamp(0.875rem, 3vw, 1rem)',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (!isExpanded) {
                          e.currentTarget.style.background = `${benefit.color}08`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                        {/* Colored Dot Icon */}
                        <div style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: benefit.color,
                          flexShrink: 0,
                          boxShadow: `0 0 0 3px ${benefit.color}20`,
                          transition: 'all 0.2s ease',
                        }} />
                        
                        {/* Label */}
                        <strong style={{
                          fontSize: 'clamp(0.875rem, 3.5vw, 0.9375rem)',
                          color: '#2c3e50',
                          fontWeight: 700,
                        }}>
                          {benefit.label}
                        </strong>
                      </div>
                      
                      {/* Chevron */}
                      <ChevronDown
                        size={18}
                        color={benefit.color}
                        strokeWidth={2.5}
                        style={{
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                          flexShrink: 0,
                        }}
                      />
                    </button>
                    
                    {/* Expanded Content */}
                    <div style={{
                      maxHeight: isExpanded ? '200px' : '0',
                      overflow: 'hidden',
                      transition: 'max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}>
                      <div style={{
                        padding: '0 clamp(0.75rem, 3vw, 0.875rem) clamp(0.75rem, 3vw, 0.875rem)',
                        paddingTop: '0.25rem',
                      }}>
                        <p style={{
                          fontSize: 'clamp(0.8125rem, 3vw, 0.875rem)',
                          color: '#64748b',
                          lineHeight: 1.6,
                          margin: 0,
                        }}>
                          {benefit.text}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA Badge - Clickable Link to Pricing */}
            <div style={{ 
              textAlign: 'center', 
              position: 'relative',
              zIndex: 1,
            }}>
              <Link href="/pricing" style={{ textDecoration: 'none' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1rem, 4vw, 1.25rem)',
                  background: 'linear-gradient(135deg, #F0F8E8, #F5FAED)',
                  border: '2px solid rgba(139, 195, 74, 0.4)',
                  borderRadius: '16px',
                  boxShadow: '0 2px 8px rgba(139, 195, 74, 0.15)',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(139, 195, 74, 0.25)';
                  e.currentTarget.style.borderColor = 'rgba(139, 195, 74, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(139, 195, 74, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(139, 195, 74, 0.4)';
                }}
                >
                  <BookHeart size={16} color="#8BC34A" strokeWidth={2.5} />
                  <p style={{ 
                    fontSize: 'clamp(0.8125rem, 3vw, 0.875rem)', 
                    color: '#2c3e50', 
                    fontWeight: 700,
                    margin: 0,
                  }}>
                    Available with <span style={{ color: '#7CB342' }}>Plus & Pro Subscriptions</span>
                  </p>
                </div>
              </Link>
            </div>
          </div>

          <style jsx>{`
            .montessori-modal-overlay {
              position: fixed !important;
              inset: 0 !important;
              z-index: 999999 !important;
            }

            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }

            @keyframes modalSlideUp {
              from {
                opacity: 0;
                transform: translateY(30px) scale(0.95);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }

            @keyframes pulse {
              0%, 100% {
                opacity: 0.3;
                transform: translate(-50%, -50%) scale(1);
              }
              50% {
                opacity: 0.5;
                transform: translate(-50%, -50%) scale(1.15);
              }
            }

            @keyframes iconFloat {
              0%, 100% {
                transform: translateY(0);
              }
              50% {
                transform: translateY(-6px);
              }
            }

            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            .montessori-modal-content {
              scrollbar-width: thin;
              scrollbar-color: rgba(139, 195, 74, 0.5) transparent;
            }

            .montessori-modal-content::-webkit-scrollbar {
              width: 6px;
            }

            .montessori-modal-content::-webkit-scrollbar-track {
              background: transparent;
            }

            .montessori-modal-content::-webkit-scrollbar-thumb {
              background: rgba(139, 195, 74, 0.5);
              border-radius: 10px;
            }

            .montessori-modal-content::-webkit-scrollbar-thumb:hover {
              background: rgba(139, 195, 74, 0.7);
            }

            @media (max-height: 700px) {
              .montessori-modal-content {
                padding: clamp(1rem, 3vw, 1.5rem);
                maxHeight: calc(100dvh - 1rem);
              }
            }

            @media (max-width: 500px) {
              .montessori-modal-content {
                maxWidth: calc(100vw - 2rem);
              }
            }

            @supports (-webkit-touch-callout: none) {
              .montessori-modal-overlay {
                padding-bottom: calc(1rem + env(safe-area-inset-bottom, 20px));
              }
              .montessori-modal-content {
                maxHeight: calc(100dvh - 2rem - env(safe-area-inset-bottom, 20px));
              }
            }
          `}</style>
        </div>,
        document.body
      )}
    </div>
  );
}

